import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useListTenantDivisions } from '@/entities/tenants-divisions/api/generated/api';
import { TenantUserRoleSchema } from '@/entities/tenantusers/api/generated/model';
import { GLOBAL_NAVIGATION_HEIGHT } from '@/shared/lib/constants';
import { useRouteSidePanelStore } from '@/shared/stores/side-panel-store';
import { Banner } from '@/shared/ui';
import { SideMenuHeader } from '@/widgets/right-side-menu/ui/SideMenuHeader';
import { ResetPasswordForm } from './ResetPasswordForm';
import { UserCreateForm } from './UserCreateForm';
import { UserEditForm } from './UserEditForm';

export const UserManagementSidePanel = () => {
  const { t } = useTranslation();
  const requestClose = useRouteSidePanelStore((state) => state.requestClose);
  const mode = useRouteSidePanelStore((state) => state.mode);

  const modeData = mode?.type === 'tenant-users' ? mode : undefined;

  // 所属課の選択肢を全件取得（年度横断）
  const { data: divisionsData } = useListTenantDivisions({ page_size: 0 });
  // 取得完了前に edit フォームをマウントすると、defaultValues に空の divisions が
  // 焼き付けられて以降反映されないため、データ取得を待ってからフォームをレンダリングする
  const divisionItems = divisionsData?.items;

  const roleOptions = useMemo(
    () => [
      { label: t('user-management.role.admin'), value: TenantUserRoleSchema.admin },
      { label: t('user-management.role.editor'), value: TenantUserRoleSchema.editor },
    ],
    [t],
  );

  const title = (() => {
    if (modeData?.mode === 'edit') return t('user-management.users.edit.title');
    if (modeData?.mode === 'reset-password') return t('user-management.users.reset-password.title');
    return t('user-management.users.create.title');
  })();

  return (
    <div
      className="flex flex-col overflow-hidden"
      style={{ height: `calc(100vh - ${GLOBAL_NAVIGATION_HEIGHT}px)` }}
    >
      <SideMenuHeader title={title} onClose={requestClose} />
      {modeData?.mode === 'create' && divisionItems !== undefined && (
        <UserCreateForm
          onClose={requestClose}
          divisionItems={divisionItems}
          roleOptions={roleOptions}
        />
      )}
      {modeData?.mode === 'edit' && divisionItems !== undefined && (
        // key で再マウントして、ユーザー切替時に defaultValues を確実に反映する
        <UserEditForm
          key={modeData.user.id}
          user={modeData.user}
          onClose={requestClose}
          divisionItems={divisionItems}
          roleOptions={roleOptions}
        />
      )}
      {modeData?.mode === 'reset-password' && (
        <ResetPasswordForm key={modeData.user.id} user={modeData.user} onClose={requestClose} />
      )}
      {!modeData && (
        <Banner
          type="error"
          title={t('common.status.error')}
          description={t('common.status.no-data')}
        />
      )}
    </div>
  );
};
